/**
 * AuthModal — Login/signup modal triggered by gated features or header Sign In
 * CSS prefix: auth-
 */

import { useState, useEffect, useRef } from 'react'
import { X, Mail, Lock, AlertCircle, CheckCircle, Loader, Chrome } from 'lucide-react'
import { useAuth } from './AuthProvider'

// SVG icons for OAuth providers
const DiscordIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
  </svg>
)

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
)

const GitHubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
  </svg>
)

const OAUTH_PROVIDERS = [
  { id: 'google', label: 'Google', icon: GoogleIcon, color: '#fff', textColor: '#333', border: '#ddd' },
  { id: 'discord', label: 'Discord', icon: DiscordIcon, color: '#5865F2', textColor: '#fff', border: '#5865F2' },
  { id: 'apple', label: 'Apple', icon: AppleIcon, color: '#000', textColor: '#fff', border: '#000' },
  { id: 'github', label: 'GitHub', icon: GitHubIcon, color: '#24292e', textColor: '#fff', border: '#24292e' },
]
import './AuthModal.css'

export default function AuthModal({ isOpen, onClose, initialTab = 'signin' }) {
  const { signIn, signUp, signInWithOAuth } = useAuth()
  const [activeTab, setActiveTab] = useState(initialTab)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const emailRef = useRef(null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setPassword('')
      setError(null)
      setSuccess(null)
      setSubmitting(false)
      setActiveTab(initialTab)
      // Focus email input after render
      setTimeout(() => emailRef.current?.focus(), 100)
    }
  }, [isOpen, initialTab])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setSubmitting(true)

    try {
      if (activeTab === 'signin') {
        await signIn(email, password)
        onClose()
      } else {
        const data = await signUp(email, password)
        // Supabase may require email confirmation
        if (data?.user?.identities?.length === 0) {
          setError('An account with this email already exists.')
        } else if (data?.user && !data?.session) {
          setSuccess('Check your email to confirm your account, then sign in.')
          setActiveTab('signin')
          setPassword('')
        } else {
          // Auto-confirmed (if email confirm is disabled in Supabase)
          onClose()
        }
      }
    } catch (err) {
      setError(err.message || 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-overlay" onClick={onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="auth-close" onClick={onClose} title="Close">
          <X size={18} />
        </button>

        {/* Tab switcher */}
        <div className="auth-tabs">
          <button
            className={`auth-tab ${activeTab === 'signin' ? 'active' : ''}`}
            onClick={() => { setActiveTab('signin'); setError(null); setSuccess(null) }}
          >
            Sign In
          </button>
          <button
            className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
            onClick={() => { setActiveTab('signup'); setError(null); setSuccess(null) }}
          >
            Sign Up
          </button>
        </div>

        {/* OAuth providers */}
        <div className="auth-oauth">
          {OAUTH_PROVIDERS.map(({ id, label, icon: Icon, color, textColor, border }) => (
            <button
              key={id}
              className="auth-oauth-btn"
              style={{ background: color, color: textColor, borderColor: border }}
              onClick={async () => {
                setError(null)
                try {
                  await signInWithOAuth(id)
                } catch (err) {
                  const msg = err.message || `Failed to sign in with ${label}`
                  // If the error contains HTML (e.g. Supabase 522 page), show a friendly message
                  if (msg.includes('<') || msg.includes('>')) {
                    setError('Authentication service temporarily unavailable. Please try again later.')
                  } else {
                    setError(msg)
                  }
                }
              }}
              type="button"
            >
              <Icon />
              <span>Continue with {label}</span>
            </button>
          ))}
        </div>

        <div className="auth-divider">
          <span>or use email</span>
        </div>

        {/* Form */}
        <form className="auth-form" onSubmit={handleSubmit}>
          {error && (
            <div className="auth-message auth-error">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="auth-message auth-success">
              <CheckCircle size={14} />
              <span>{success}</span>
            </div>
          )}

          <div className="auth-field">
            <label className="auth-label" htmlFor="auth-email">
              <Mail size={14} /> Email
            </label>
            <input
              ref={emailRef}
              id="auth-email"
              className="auth-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="auth-password">
              <Lock size={14} /> Password
            </label>
            <input
              id="auth-password"
              className="auth-input"
              type="password"
              placeholder={activeTab === 'signup' ? 'Min 6 characters' : 'Your password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={activeTab === 'signin' ? 'current-password' : 'new-password'}
            />
          </div>

          <button
            className="auth-submit"
            type="submit"
            disabled={submitting}
          >
            {submitting ? (
              <><Loader size={14} className="auth-spinner" /> Working...</>
            ) : (
              activeTab === 'signin' ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        <div className="auth-footer">
          {activeTab === 'signin' ? (
            <span>
              No account?{' '}
              <button className="auth-link" onClick={() => { setActiveTab('signup'); setError(null) }}>
                Create one
              </button>
            </span>
          ) : (
            <span>
              Already have an account?{' '}
              <button className="auth-link" onClick={() => { setActiveTab('signin'); setError(null) }}>
                Sign in
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
